/**
 * What a per-period budget reading concluded -- including when it concluded
 * nothing.
 *
 * Three of the six statuses are verdicts and three are not, and the distinction
 * is the whole reason this component exists rather than a raw string in a cell.
 * `budget_not_interpretable` is a DELIBERATE REFUSAL: the same number reads as a
 * monthly allowance and as a lifetime budget, those two readings disagree about
 * whether the demand is over, and nothing in the data settles it. Ten of the
 * sixteen live rows are in that state.
 *
 * So it gets its own visual language -- a neutral chip carrying a question
 * mark, neither the green of "within" nor the red of "over"; the icon, not the
 * colour, is what keeps it apart from the missing-data chips, which carry a
 * minus. A refusal rendered as calm reads as a pass,
 * and a refusal rendered as an alarm reads as a bug; it is neither. The engine's
 * `reason` sits beside it on the row, because the contradiction is specific to
 * each demand and only the sentence can say which one it is.
 */

import { CircleHelp, MinusCircle, TrendingUp } from "lucide-react";
import { type ChipTone, StatusChip } from "@/components/shared/status-chip";
import type { RiskPerPeriodRow } from "@/lib/profitability-api";

interface StatusSpec {
  label: string;
  tone: ChipTone;
  title: string;
  icon?: typeof CircleHelp;
}

const STATUS: Record<string, StatusSpec> = {
  over_period_budget: {
    label: "Over monthly budget",
    tone: "bad",
    icon: TrendingUp,
    title:
      "Mean hours a month are above the demand's monthly allowance. A rate, not a total -- there is no lifetime overrun on a per-period budget and no dollars are put against it.",
  },
  within_period_budget: {
    label: "Within monthly budget",
    tone: "good",
    title:
      "Mean hours a month are at or below the demand's monthly allowance. It says nothing about a lifetime total, which is not a thing this budget has.",
  },
  budget_not_interpretable: {
    label: "No verdict",
    tone: "neutral",
    icon: CircleHelp,
    title:
      "Not a pass and not a fault. The same budget figure reads one way as a monthly allowance and another as a budget for the life of the demand, the two readings disagree about whether it is over, and this data cannot settle which is right -- so no over-or-under verdict is stated. The reason on the row names the specific contradiction.",
  },
  no_elapsed_months: {
    label: "No elapsed months",
    tone: "neutral",
    icon: MinusCircle,
    title:
      "No closed month has elapsed since the demand started, so there is no monthly mean to compare against the allowance yet.",
  },
  no_start_date: {
    label: "No start date",
    tone: "neutral",
    icon: MinusCircle,
    title:
      "The demand has no start date, so elapsed months cannot be counted and a monthly mean cannot be formed.",
  },
  unreadable_dates: {
    label: "Dates unreadable",
    tone: "neutral",
    icon: MinusCircle,
    title:
      "The dates on the demand could not be read, so elapsed months cannot be counted. This is a data defect on the demand, not a finding about the work.",
  },
};

export function PerPeriodStatusBadge({ status }: { status: string }) {
  const spec = STATUS[status];
  // An unmapped status is a service change, not a reason to render nothing:
  // show the raw value so a new one is visible rather than silently blank.
  if (!spec) {
    return <StatusChip tone="neutral">{status.replace(/_/g, " ")}</StatusChip>;
  }
  const Icon = spec.icon;
  return (
    <StatusChip tone={spec.tone} title={spec.title}>
      {Icon ? <Icon className="size-3" aria-hidden /> : null}
      {spec.label}
    </StatusChip>
  );
}

/**
 * Whether the per-period reading was measured or guessed.
 *
 * `demand_per_period` is structural -- the demand's hours budget and its
 * per-period default are the same number, which is the demand saying so.
 * `demand_per_period_by_guidance` is a ratio heuristic over the guidance plan,
 * and eleven of the sixteen live rows rest on it. That inference is precisely
 * why most of this bucket refuses a verdict, so a reader who cannot tell the
 * two apart cannot judge how much weight any row deserves.
 *
 * Plain text rather than a second coloured pill: the status badge beside it
 * already carries the row's one colour, and two pills per row turn a board into
 * a wall of badges.
 */
export function PerPeriodSourceCell({
  source,
  evidence,
}: {
  source: string;
  /** Optional: the roll-up table has no room for it, the drill-in does. */
  evidence?: RiskPerPeriodRow["per_period_evidence"];
}) {
  const inferred = source === "demand_per_period_by_guidance";
  const measured = source === "demand_per_period";
  if (!measured && !inferred) {
    return (
      <span
        className="whitespace-nowrap text-xs text-muted-foreground"
        title={`${source}: not one of the two per-period tests this screen knows about, so how the budget was read is not stated here.`}
      >
        {source.replace(/_/g, " ")}
      </span>
    );
  }
  return (
    <span
      className={`whitespace-nowrap text-xs ${inferred ? "italic text-muted-foreground" : "font-medium text-foreground"}`}
      title={
        inferred
          ? "INFERRED, not measured: summed guidance over two or more months reaches three times the budget, so the budget was read as a per-period allowance by heuristic. The demand itself does not say so."
          : "MEASURED off the demand: its hours budget and its per-period default are the same number, which is the demand stating that the budget is an allowance per period."
      }
    >
      {inferred ? "Inferred" : "Measured"}
      {/* The ratio, where there is one and there is room for it. A reader
          judging whether to trust an inferred row needs to know it fired at
          3.32x rather than 60x -- "Inferred" alone reads the same either way,
          and 3.32 against a threshold of 3.0 is the known false positive. */}
      {inferred && evidence?.guidance_to_budget_ratio ? (
        <span className="ml-1 not-italic tabular-nums">
          {evidence.guidance_to_budget_ratio.toFixed(2)}x
          {evidence.guidance_months ? ` over ${evidence.guidance_months} mo` : ""}
        </span>
      ) : null}
    </span>
  );
}
